import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Check, ShieldCheck } from "lucide-react";
import { authFetch } from "../lib/auth";
import { isVerificationExempt, verificationNext } from "../lib/verification";
import { useVerificationStatus } from "../hooks/useVerificationStatus";
import VerificationStatusNotice from "../components/design/VerificationStatusNotice";
import BankSelect from "../components/BankSelect";
import AuthFrame from "../components/design/AuthFrame";
import { verificationRequestContext } from "../lib/verificationNavigation";

type StartResponse = { ok?: boolean; requestId?: string; error?: string };
type ConfirmResponse = { ok?: boolean; error?: string };

export default function AccountVerify() {
  const location = useLocation();
  const { account, loading, error, retry } = useVerificationStatus();
  const next = verificationNext(new URLSearchParams(location.search).get("next"));
  if (loading || error || !account) return <VerificationStatusNotice error={error} retry={retry} />;
  if (isVerificationExempt(account) || account.account_status === "verified") return <Navigate to={next} replace />;
  return <AccountVerifyForm key={account.id} next={next} />;
}

function AccountVerifyForm({ next }: { next: string }) {
  const nav = useNavigate();
  const { amountEntry } = verificationRequestContext(next);
  const [bankCode, setBankCode] = useState("004");
  const [accountNo, setAccountNo] = useState("");
  const [accountName, setAccountName] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"start" | "confirm">("start");
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);
  const finished = useRef(false);
  const mounted = useRef(true);
  const redirectTimer = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { if (step === "confirm") codeRef.current?.focus(); }, [step]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (redirectTimer.current !== null) window.clearTimeout(redirectTimer.current);
    };
  }, []);

  async function handleStart(event: FormEvent) {
    event.preventDefault();
    if (submitting.current || finished.current) return;
    setError(null);
    if (!bankCode || !accountNo || !accountName) {
      setError("은행, 계좌번호, 예금주명을 입력해 주세요.");
      return;
    }
    submitting.current = true;
    setLoading(true);
    try {
      const res = await authFetch("/api/onewon/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bankCode, accountNo, accountName }),
      });
      const data: StartResponse = await res.json().catch(() => ({}));
      if (!mounted.current) return;
      if (!res.ok || !data.ok || !data.requestId) {
        setError(data.error || "1원 송금 요청에 실패했습니다.");
        return;
      }
      setRequestId(data.requestId);
      setStep("confirm");
      setCode("");
    } catch {
      if (mounted.current) setError("1원 송금 요청 중 오류가 발생했습니다.");
    } finally {
      submitting.current = false;
      if (mounted.current) setLoading(false);
    }
  }

  async function handleConfirm(event: FormEvent) {
    event.preventDefault();
    if (submitting.current || finished.current) return;
    setError(null);
    if (!requestId) {
      setError("요청 번호가 없습니다. 처음부터 다시 진행해 주세요.");
      return;
    }
    if (!code.trim()) {
      setError("송금자명을 입력해 주세요.");
      return;
    }
    submitting.current = true;
    setLoading(true);
    try {
      const res = await authFetch("/api/onewon/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId, verifyValue: code.trim() }),
      });
      const data: ConfirmResponse = await res.json().catch(() => ({}));
      if (!mounted.current) return;
      if (!res.ok) {
        setError(data.error || "인증문자 확인에 실패했습니다.");
        return;
      }
      if (!data.ok) {
        setError(data.error || "인증문자가 일치하지 않습니다.");
        return;
      }
      finished.current = true;
      setCompleted(true);
      redirectTimer.current = window.setTimeout(() => nav(next, { replace: true }), 1500);
    } catch {
      if (mounted.current) setError("인증문자 확인 중 오류가 발생했습니다.");
    } finally {
      submitting.current = false;
      if (mounted.current) setLoading(false);
    }
  }

  function handleBack() {
    if (submitting.current || finished.current) return;
    nav(next, { replace: true });
  }

  const confirming = step === "confirm";
  const busy = loading || completed;
  return <AuthFrame title="1원 계좌 인증" heading={completed ? <>계좌 인증이<br />완료되었어요.</> : confirming ? <>입금 내역에서<br />입금자명을 확인해 주세요.</> : <>본인 명의 계좌를<br />확인해 주세요.</>}
    description={completed ? "잠시 후 진행하던 화면으로 돌아갑니다." : confirming ? "1원이 입금된 거래내역의 입금자명을 그대로 입력해 주세요." : "입력한 계좌로 1원을 보내 계좌 소유 여부를 확인합니다."}
    onBack={handleBack} busy={busy} steps={amountEntry ? ["송금 금액", "정보·서류", "최종 확인"] : undefined} step={1} progressLabel="결제 요청">
    <div className="ja-verification-steps" aria-label="계좌 인증 단계">
      <span className={!confirming || completed ? "is-active" : ""} aria-current={!confirming && !completed ? "step" : undefined}>{confirming || completed ? <Check size={15} /> : "01"} 계좌 정보</span>
      <span className={confirming ? "is-active" : ""} aria-current={confirming && !completed ? "step" : undefined}>{completed ? <Check size={15} /> : "02"} 입금자명 확인</span>
    </div>
    {error && <div ref={errorRef} tabIndex={-1} className="ja-alert is-error" role="alert">{error}</div>}
    {completed ? <div className="ja-complete" role="status"><span className="ja-complete-icon"><ShieldCheck size={30} /></span><p>본인 명의 계좌 확인을 마쳤습니다.<br />다음 화면으로 이동하고 있어요.</p></div> : !confirming ? <form className="ja-form" onSubmit={handleStart} aria-busy={loading}>
      <BankSelect label="은행" value={bankCode} onChange={setBankCode} required disabled={loading} className="ja-field" />
      <div className="ja-field"><label htmlFor="verify-account-number">계좌번호</label><input id="verify-account-number" value={accountNo} onChange={event => setAccountNo(event.target.value)} inputMode="numeric" autoComplete="off" placeholder="계좌번호를 입력해 주세요" required disabled={loading} /></div>
      <div className="ja-field"><label htmlFor="verify-account-holder">예금주명</label><input id="verify-account-holder" value={accountName} onChange={event => setAccountName(event.target.value)} autoComplete="name" placeholder="본인 명의의 예금주명을 입력해 주세요" required disabled={loading} /></div>
      <div className="ja-alert">실제로 이용 중인 본인 명의의 계좌를 입력해 주세요. 다음 단계에서 은행 앱의 입금 내역을 확인합니다.</div>
      <button type="submit" className="ja-primary" disabled={loading}>{loading ? "1원 송금 요청 중…" : "1원 인증 시작하기"}</button>
    </form> : <form className="ja-form" onSubmit={handleConfirm} aria-busy={loading}>
      <div className="ja-alert is-success"><strong>1원 송금을 요청했어요.</strong>{accountName}님 계좌의 입금 내역을 확인해 주세요.</div>
      <div className="ja-field"><label htmlFor="verify-account-code">입금자명</label><input ref={codeRef} id="verify-account-code" value={code} onChange={event => setCode(event.target.value)} autoComplete="off" placeholder="거래내역에 표시된 입금자명" aria-describedby="verify-account-code-hint" required disabled={loading} /><small id="verify-account-code-hint" className="ja-hint">1원 입금 거래에 표시된 입금자명을 그대로 입력해 주세요.</small></div>
      <button type="submit" className="ja-primary" disabled={loading}>{loading ? "입금자명 확인 중…" : "인증 완료하기"}</button>
    </form>}
    {!completed && <div className="ja-prepare"><button type="button" className="ja-secondary" onClick={handleBack} disabled={loading}>이전 단계로 돌아가기</button></div>}
  </AuthFrame>;
}
